import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BottomSheet } from 'components/mobile/BottomSheet/BottomSheet';
import {
  isIOS,
  isStandalone,
  promptInstall,
  useInstallKind,
} from 'lib/installApp';
import styles from './InstallApp.module.css';

/** Menu items fire this; the one <InstallApp /> in App answers it. */
const OPEN_EVENT = 'tottalo:install-app';
export const openInstallApp = () => window.dispatchEvent(new Event(OPEN_EVENT));

/** The banner stays away this long after "Later". */
const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;
const SNOOZE_KEY = 'install-banner-snoozed';
/** Shown a moment after the page opens, not over the first paint. */
const BANNER_DELAY_MS = 4_000;
/** The arrow towards Safari's share button stays this long. */
const ARROW_MS = 10_000;

const snoozed = () => {
  try {
    const at = Number(localStorage.getItem(SNOOZE_KEY));
    return at > 0 && Date.now() - at < SNOOZE_MS;
  } catch {
    return false;
  }
};

const snooze = () => {
  try {
    localStorage.setItem(SNOOZE_KEY, String(Date.now()));
  } catch {
    /* private mode: the banner simply comes back */
  }
};

/** A phone or tablet; desktop browsers install from the menu item only. */
const touchDevice = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(pointer: coarse)').matches;

const ShareIcon = () => (
  <svg viewBox="0 0 24 24" className={styles.inlineIcon} aria-hidden>
    <path
      d="M12 3v12M8 7l4-4 4 4M6 11H5v10h14V11h-1"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const AddIcon = () => (
  <svg viewBox="0 0 24 24" className={styles.inlineIcon} aria-hidden>
    <rect
      x="4"
      y="4"
      width="16"
      height="16"
      rx="4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    />
    <path
      d="M12 8.5v7M8.5 12h7"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

/**
 * "Install the app": a banner on phones and the steps for iPhone. Where the
 * browser allows it (Android Chrome, Samsung, Edge) one tap opens its own
 * install dialog; on iPhone and iPad a page cannot install itself, so the
 * player is shown the two taps in Safari's share menu, with an arrow
 * towards the share button once the sheet is closed.
 */
export const InstallApp = () => {
  const { t } = useTranslation();
  const kind = useInstallKind();
  const [banner, setBanner] = useState(false);
  // Which steps are open: the browser's own (kind), the manual ones when
  // the browser offers no install dialog, or "already installed".
  const [sheet, setSheet] = useState<
    'ios' | 'ios-in-app' | 'manual' | 'installed' | null
  >(null);
  const [arrow, setArrow] = useState(false);
  const [copied, setCopied] = useState(false);

  const open = useCallback(async () => {
    setBanner(false);
    if (kind === 'prompt') {
      await promptInstall();
      return;
    }
    // The menu item is always there (owner, 2026-10-30): a browser that
    // offers no install dialog gets the steps through its own menu.
    setSheet(kind ?? (isStandalone() ? 'installed' : 'manual'));
  }, [kind]);

  useEffect(() => {
    window.addEventListener(OPEN_EVENT, open);
    return () => window.removeEventListener(OPEN_EVENT, open);
  }, [open]);

  useEffect(() => {
    if (!kind || !touchDevice() || snoozed()) {
      setBanner(false);
      return;
    }
    const id = window.setTimeout(() => setBanner(true), BANNER_DELAY_MS);
    return () => window.clearTimeout(id);
  }, [kind]);

  useEffect(() => {
    if (!arrow) return;
    const id = window.setTimeout(() => setArrow(false), ARROW_MS);
    return () => window.clearTimeout(id);
  }, [arrow]);

  const closeSheet = useCallback(() => setSheet(null), []);
  const understood = () => {
    setSheet(null);
    // iPhone Safari has its bar at the bottom; on iPad it is at the top.
    if (kind === 'ios' && !/iPad|Macintosh/.test(navigator.userAgent)) {
      setArrow(true);
    }
  };
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.origin + '/');
      setCopied(true);
    } catch {
      /* no clipboard: the steps still say what to do */
    }
  };

  return (
    <>
      {banner && (
        <div className={styles.banner} role="dialog">
          <img src="/apple-touch-icon.png" alt="" className={styles.appIcon} />
          <div className={styles.bannerText}>
            <strong>{t('install_banner_title')}</strong>
            <span>{t('install_banner_text')}</span>
          </div>
          <button type="button" className={styles.primary} onClick={open}>
            {t('install_banner_button')}
          </button>
          <button
            type="button"
            className={styles.dismiss}
            aria-label={t('install_later')}
            onClick={() => {
              snooze();
              setBanner(false);
            }}
          >
            ✕
          </button>
        </div>
      )}

      {sheet === 'ios' && (
        <BottomSheet title={t('install_ios_title')} close={closeSheet}>
          <div className={styles.sheet}>
            <div className={styles.appRow}>
              <img
                src="/apple-touch-icon.png"
                alt=""
                className={styles.appIconLarge}
              />
              <div>
                <strong>Tottalo Bet</strong>
                <span>{t('install_ios_intro')}</span>
              </div>
            </div>
            <ol className={styles.steps}>
              <li>
                <span className={styles.stepNo}>1</span>
                <span className={styles.stepIcon}>
                  <ShareIcon />
                </span>
                <span>{t('install_ios_step1')}</span>
              </li>
              <li>
                <span className={styles.stepNo}>2</span>
                <span className={styles.stepIcon}>
                  <AddIcon />
                </span>
                <span>{t('install_ios_step2')}</span>
              </li>
              <li>
                <span className={styles.stepNo}>3</span>
                <span className={styles.stepIcon}>
                  <strong>{t('install_ios_add')}</strong>
                </span>
                <span>{t('install_ios_step3')}</span>
              </li>
            </ol>
            <button
              type="button"
              className={styles.primaryWide}
              onClick={understood}
            >
              {t('install_ios_ok')}
            </button>
          </div>
        </BottomSheet>
      )}

      {sheet === 'ios-in-app' && (
        <BottomSheet title={t('install_in_app_title')} close={closeSheet}>
          <div className={styles.sheet}>
            <p className={styles.text}>{t('install_in_app_text')}</p>
            <button
              type="button"
              className={styles.primaryWide}
              onClick={copyLink}
            >
              {copied ? t('install_link_copied') : t('install_copy_link')}
            </button>
          </div>
        </BottomSheet>
      )}

      {sheet === 'manual' && (
        <BottomSheet title={t('install_manual_title')} close={closeSheet}>
          <div className={styles.sheet}>
            <div className={styles.appRow}>
              <img
                src="/apple-touch-icon.png"
                alt=""
                className={styles.appIconLarge}
              />
              <div>
                <strong>Tottalo Bet</strong>
                <span>{t('install_manual_intro')}</span>
              </div>
            </div>
            <ul className={styles.manual}>
              <li>
                <strong>Android</strong>
                <span>{t('install_manual_android')}</span>
              </li>
              <li>
                <strong>{t('install_manual_computer')}</strong>
                <span>{t('install_manual_desktop')}</span>
              </li>
              <li>
                <strong>{t('install_manual_other_title')}</strong>
                <span>{t('install_manual_other')}</span>
              </li>
            </ul>
            <button
              type="button"
              className={styles.primaryWide}
              onClick={copyLink}
            >
              {copied ? t('install_link_copied') : t('install_copy_link')}
            </button>
          </div>
        </BottomSheet>
      )}

      {sheet === 'installed' && (
        <BottomSheet title={t('install_installed_title')} close={closeSheet}>
          <div className={styles.sheet}>
            <p className={styles.text}>{t('install_installed_text')}</p>
            <button
              type="button"
              className={styles.primaryWide}
              onClick={closeSheet}
            >
              {t('install_ios_ok')}
            </button>
          </div>
        </BottomSheet>
      )}

      {arrow && isIOS() && (
        <div
          className={styles.arrow}
          role="status"
          onClick={() => setArrow(false)}
        >
          <span className={styles.arrowBubble}>
            <ShareIcon /> {t('install_ios_arrow')}
          </span>
          <span className={styles.arrowHead} aria-hidden>
            ↓
          </span>
        </div>
      )}
    </>
  );
};
